// Main entry point for The Architect Vite application
import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient.js';
import './styles/main.css';
import AppRouter from './router.jsx';
import { AuthProvider } from './contexts/AuthContext.jsx';
import { WorkflowProvider } from './contexts/WorkflowContext.jsx';
// import CreditNotifications from './components/CreditNotifications.jsx'; // Disabled - using controlled credit alerts instead
import CreditManager from './components/CreditManager.jsx';
import CookieConsent from './components/CookieConsent.jsx';
import api from './utils/api.js';

// IMMEDIATE: Fix stuck scroll from previous session (runs before React mounts)
// This prevents the "frozen page" issue when reloading during workflow
(function resetStuckBodyScroll() {
    if (document.body.style.position === 'fixed') {
        const savedScrollY = document.body.style.top;
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        if (savedScrollY) {
            window.scrollTo(0, parseInt(savedScrollY || '0', 10) * -1);
        }
    }
})();


// Global app state
window.APP = {
    currentSessionId: null
};

// Global utilities that the original app expects
window.getSessionId = () => {
    return window.APP.currentSessionId || sessionStorage.getItem('currentSessionId');
};

window.setSessionId = (sessionId) => {
    window.APP.currentSessionId = sessionId;
    sessionStorage.setItem('currentSessionId', sessionId);
};

// Toast notification function (used by components)
window.showToast = (message, type = 'success') => {
    // Remove existing toast
    const existingToast = document.querySelector('.toast');
    if (existingToast) {
        existingToast.remove();
    }

    // Create new toast
    const toast = document.createElement('div');
    toast.className = `toast ${type === 'error' ? 'error' : ''}`;
    toast.textContent = message;
    
    document.body.appendChild(toast);
    
    // Show toast
    setTimeout(() => toast.classList.add('show'), 100);
    
    // Hide toast after 3 seconds
    setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => toast.remove(), 300);
    }, 3000);
};

// Make API available globally for components that need it
window.api = api;

// Production mode - mock data functionality removed

// Error Boundary Component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Error logged to browser for debugging in development only
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="text-red-500 text-4xl">⚠️</div>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Application Error
                                </h3>
                                <div className="mt-2 text-sm text-gray-500">
                                    <p>Something went wrong with The Architect application.</p>
                                </div>
                                <div className="mt-4">
                                    <button
                                        onClick={() => window.location.reload()}
                                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                                    >
                                        Reload Application
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Initialize React App
const root = ReactDOM.createRoot(document.getElementById('app'));
root.render(
    <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
            <AuthProvider>
                <WorkflowProvider>
                    <CreditManager>
                        <AppRouter />
                        <CookieConsent />
                        {/* <CreditNotifications /> */} {/* Disabled - using controlled credit alerts instead */}
                    </CreditManager>
                </WorkflowProvider>
            </AuthProvider>
        </ErrorBoundary>
    </QueryClientProvider>
); 